/**
 * Widget régulateur : les grandeurs que l'utilisateur a choisies à la configuration, affichées
 * en permanence, plus une courbe de tendance.
 *
 * Widget PASSIF (décision UX du 2026-07-30) : ni onglet, ni sélecteur, aucun clic à faire.
 * L'éleveur ouvre son dashboard et lit ses 8 grandeurs — tout le choix est fait une fois pour
 * toutes dans le menu de création/modification (voir REGULATOR_METRIC_OPTIONS).
 *
 * Les données viennent de MongoDB (BIGDATA) via le relais DATA `/devices/{uuid}/measures`
 * — voir useRegulatorMeasures. Règles :
 *  - aucun indicateur n'a de valeur de repli : ce qui n'existe pas s'affiche « — », et
 *    « Non autorisé » quand le droit bigdata manque ;
 *  - une grandeur cochée reste affichée même sans donnée (la mise en page ne bouge pas) ;
 *  - la nature du chiffre dépend de la grandeur : l'AMBIANCE affiche le DERNIER relevé
 *    (surveillance — une moyenne noierait un pic nocturne), la CONSO et l'ÉNERGIE des
 *    moyennes journalières (débits), l'effectif et le stock des états instantanés.
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAccountStorage } from "../../../../hooks/useAccountStorage";
import { useDeviceConnections } from "../../../../hooks/useDeviceConnections";
import { remoteDeviceRoute } from "../../../../utils/roles";
import { Icon, IconsList } from "../../../common/Icon";
import { ConnectionDot, isRemoteBlocked } from "../../device/ConnectionDot";
import {
  isLegacyFirmwareType,
  METRIC_COLORS,
  metricAccessRight,
} from "../widgetCatalog";
import { WidgetPreviewContext, WidgetShell } from "./WidgetShell";
import {
  RegulatorChartPoint,
  RegulatorChartType,
  RegulatorMeasureIndicators,
  RegulatorMetric,
  MeasureRange,
  rangeValue,
  useRegulatorMeasures,
} from "./useRegulatorMeasures";

type RegulatorStatus = "ok" | "warning" | "critical";

export type {
  RegulatorChartType,
  RegulatorChartPoint,
} from "./useRegulatorMeasures";

export type RegulatorWidgetProps = {
  /**
   * RÉFÉRENCE du widget : l'uuid du régulateur (décision du 2026-07-30 — le widget porte sur un
   * produit, pas sur un bâtiment). C'est la clé de tout : connexion à distance directement, et
   * mesures via le bâtiment puis le lot ouvert (les mesures Mongo sont indexées par lot).
   */
  deviceUuid?: string;
  name: string;
  tag?: string;
  status?: RegulatorStatus;
  statusLabel?: string;
  layoutVariant?: "compact" | "standard" | "full";
  /**
   * Grandeurs à afficher, dans l'ordre choisi par l'utilisateur. Limitée à 8 par le
   * configurateur (`maxIndicators`), mais le widget n'impose rien de son côté.
   */
  metrics?: RegulatorMetric[];
  /**
   * Grandeurs mises en avant (grandes tuiles en tête). Doivent faire partie de `metrics` ;
   * le widget les ignore sinon. Les autres grandeurs passent en grille compacte dessous.
   */
  primaryMetric?: RegulatorMetric;
  secondaryMetric?: RegulatorMetric;
  showChart?: boolean;
  chartType?: RegulatorChartType;
};

const statusStyles: Record<
  RegulatorStatus,
  { dot: string; badge?: string; badgeText?: string }
> = {
  ok: { dot: "bg-secondary" },
  warning: {
    dot: "bg-warning",
    // `text-warning` sur `bg-warning/20` ne donnait que 1.84:1 — un badge d'alerte illisible
    // est exactement celui qu'il ne faut pas rater. `warning-readable` monte à 4.16:1.
    badge: "bg-warning/20 text-warning-readable",
    badgeText: "Alerte",
  },
  critical: {
    dot: "bg-red-500",
    // red-600 sur red-100 = 3.95:1 ; red-700 sur red-100 = 5.9:1.
    badge: "bg-red-100 text-red-700",
    badgeText: "Alerte critique",
  },
};

const CHART_CONFIG: Record<
  RegulatorChartType,
  { label: string; unit: string; color: string; digits?: number }
> = {
  temperatureInside: {
    label: "Température int.",
    unit: "°C",
    color: "#3F4A18",
    digits: 1,
  },
  temperatureOutside: {
    label: "Température ext.",
    unit: "°C",
    color: "#7a8a3a",
    digits: 1,
  },
  humidityInside: { label: "Hygrométrie int.", unit: "%", color: "#0976a5" },
  humidityOutside: { label: "Hygrométrie ext.", unit: "%", color: "#0b5b7d" },
  co2: { label: "CO₂", unit: " ppm", color: "#6B6B6B" },
  nh3: { label: "NH₃", unit: " ppm", color: "#b08968" },
  airflow: { label: "Débit d'air", unit: " m³", color: "#4a9db5" },
  light: { label: "Éclairement", unit: " min", color: "#9C7400" },
  weight: { label: "Poids moyen", unit: " g", color: "#B71C52" },
  gmq: { label: "GMQ", unit: " g/j", color: "#8e1a40" },
  dead: { label: "Mortalité", unit: "", color: "#c0392b" },
  animalsTotal: { label: "Effectif", unit: "", color: "#6b7280" },
  water: { label: "Eau", unit: " L", color: "#1e90d6" },
  food: { label: "Aliment", unit: " kg", color: "#B71C52" },
  siloStock: { label: "Stock silo", unit: " kg", color: "#a0803a" },
  gaz: { label: "Gaz", unit: " m³", color: "#AD4529" },
  electricity: { label: "Électricité", unit: " kW", color: "#3d5a80" },
};

/**
 * Teinte de l'icône d'une grandeur. Source unique dans le catalogue (`METRIC_COLORS`), pour
 * que la couleur soit la même dans le configurateur et dans le widget.
 */
function metricColor(metric: RegulatorMetric): string | undefined {
  return METRIC_COLORS[metric];
}

/** Formate une valeur absente en « — » : aucun chiffre inventé sur un écran d'élevage. */
function formatValue(value: number | undefined, unit = "", digits = 0): string {
  if (value === undefined) return "—";
  return `${value.toFixed(digits)}${unit}`;
}

/**
 * Rend une plage « 18.2 → 24.1°C ». L'amplitude est le vrai signal de conduite d'élevage :
 * un écart jour/nuit de 6 °C est un stress thermique, même avec une moyenne correcte.
 * Si une seule borne a été mesurée, ou si les deux coïncident, on n'affiche qu'un chiffre
 * plutôt qu'une plage trompeuse.
 */
function formatRange(
  range: MeasureRange | undefined,
  unit: string,
  digits = 0
): string {
  if (!range) return "—";
  const { min, max } = range;
  if (min !== undefined && max !== undefined && min !== max) {
    return `${min.toFixed(digits)} → ${max.toFixed(digits)}${unit}`;
  }
  return formatValue(max ?? min, unit, digits);
}

/** Ligne d'indicateur : libellé à gauche, valeur à droite. Masquée si aucune valeur. */
const MetricLine = ({
  icon,
  faIcon,
  label,
  value,
  hint,
  color,
}: {
  icon?: IconsList;
  faIcon?: string;
  label: string;
  value: string;
  hint?: string;
  /** Teinte de l'icône (couleur de la grandeur). Seule l'ICÔNE est colorée : teinter le
   *  libellé aussi nuirait à la lisibilité et ferait un arc-en-ciel sur 8 lignes. */
  color?: string;
}) => (
  // Accessibilité : libellé en `text-sm` sur `grey-readable` (5.74:1) et non `text-xs` sur
  // `grey-medium` (3.07:1, sous le seuil AA) ; icônes à 1rem au lieu de 0.75rem. Les éleveurs
  // consultent ce widget de loin, parfois avec une vue basse.
  <div className="flex items-baseline justify-between gap-2" title={hint}>
    <span className="flex items-center gap-2 text-sm text-grey-readable min-w-0">
      {faIcon && (
        <FontAwesomeIcon
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          icon={faIcon as any}
          className="shrink-0 w-4"
          style={color ? { color } : undefined}
        />
      )}
      {icon && (
        <span style={color ? { color } : undefined} className="shrink-0 flex">
          <Icon icon={icon} size={1} unit="rem" />
        </span>
      )}
      <span className="truncate">{label}</span>
    </span>
    <span className="text-base font-oxaniumMedium text-default-text shrink-0">
      {value}
    </span>
  </div>
);

type MetricLineData = {
  label: string;
  value: string;
  faIcon?: string;
  icon?: IconsList;
  hint?: string;
};

/**
 * Découpe la valeur d'une grandeur pour la mise en avant : le chiffre à afficher en gros et,
 * s'il s'agit d'une plage, le détail « 18.2 → 24.1 » en dessous. Une plage écrite en grand
 * perdrait tout l'effet visuel de la tuile.
 */
function splitForHighlight(
  metric: RegulatorMetric,
  indicators: RegulatorMeasureIndicators
): { main: string; detail?: string } {
  const line = renderMetric(metric, indicators);
  const RANGES: Partial<Record<RegulatorMetric, MeasureRange | undefined>> = {
    temperatureInside: indicators.temperatureInside,
    temperatureOutside: indicators.temperatureOutside,
    humidityInside: indicators.humidityInside,
    humidityOutside: indicators.humidityOutside,
    co2: indicators.co2,
    nh3: indicators.nh3,
  };
  const range = RANGES[metric];
  if (
    range &&
    range.min !== undefined &&
    range.max !== undefined &&
    range.min !== range.max
  ) {
    const digits = metric.startsWith("temperature") ? 1 : 0;
    const unit = CHART_CONFIG[metric as RegulatorChartType]?.unit ?? "";
    return {
      main: `${range.max.toFixed(digits)}${unit}`,
      detail: `${range.min.toFixed(digits)} → ${range.max.toFixed(digits)}`,
    };
  }
  return { main: line.value };
}

/**
 * Tuile de mise en avant : gros chiffre, libellé au-dessus, plage en dessous quand la grandeur
 * en a une. C'est ce que l'éleveur doit lire sans chercher.
 */
const HighlightTile = ({
  label,
  main,
  detail,
  faIcon,
  icon,
  hint,
  color,
  wide = false,
}: {
  label: string;
  main: string;
  detail?: string;
  faIcon?: string;
  icon?: IconsList;
  hint?: string;
  color?: string;
  /**
   * Vrai quand la tuile est SEULE sur sa ligne. N'affecte QUE la largeur (via flex-1) : la
   * typographie reste identique à 2 vedettes, sinon le contenu débordait et faisait scroller.
   * Exposé en `data-wide` pour les tests.
   */
  wide?: boolean;
}) => (
  <div
    // Liseré gauche à la couleur de la grandeur : identifie la tuile d'un coup d'œil sans
    // colorer tout le fond (qui écraserait la lisibilité du gros chiffre).
    data-testid="highlight-tile"
    data-wide={wide ? "true" : "false"}
    className="flex-1 min-w-0 rounded-lg border border-grey-medium/40 bg-grey-light/20 px-2.5 py-2 border-l-4"
    style={color ? { borderLeftColor: color } : undefined}
    title={hint}
  >
    {/* Accessibilité : plus de `text-[11px]`/`text-[10px]` (trop petits) ni de `grey-medium`
        (3.07:1, sous AA). Libellé `text-sm`, détail `text-xs`, tous deux sur `grey-readable`. */}
    <p className="flex items-center gap-2 text-sm text-grey-readable mb-1">
      {faIcon && (
        <FontAwesomeIcon
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          icon={faIcon as any}
          className="shrink-0 w-4"
          style={color ? { color } : undefined}
        />
      )}
      {icon && (
        <span style={color ? { color } : undefined} className="shrink-0 flex">
          <Icon icon={icon} size={1} unit="rem" />
        </span>
      )}
      <span className="truncate">{label}</span>
    </p>
    {/* Typo IDENTIQUE qu'il y ait 1 ou 2 vedettes : seule la largeur de la tuile change.
        Agrandir le chiffre selon le nombre de vedettes faisait déborder et scroller. */}
    <p className="text-2xl leading-none font-oxaniumMedium text-default-text truncate">
      {main}
    </p>
    {detail && (
      <p className="text-xs text-grey-readable mt-1 truncate">{detail}</p>
    )}
  </div>
);

/**
 * Rend UNE grandeur, quelle que soit sa famille. Table de correspondance unique
 * grandeur → (libellé, icône, valeur formatée), partagée par les tuiles vedettes et la
 * grille compacte.
 */
function renderMetric(
  metric: RegulatorMetric,
  indicators: RegulatorMeasureIndicators
): MetricLineData {
  switch (metric) {
    case "temperatureInside":
      return {
        faIcon: "thermometer-half",
        label: "Température int.",
        value: formatRange(indicators.temperatureInside, "°C", 1),
        hint: "Min et max relevés le dernier jour mesuré",
      };
    case "temperatureOutside":
      return {
        faIcon: "sun",
        label: "Température ext.",
        value: formatRange(indicators.temperatureOutside, "°C", 1),
      };
    case "humidityInside":
      return {
        faIcon: "tint",
        label: "Hygrométrie int.",
        value: formatRange(indicators.humidityInside, "%"),
      };
    case "humidityOutside":
      return {
        faIcon: "droplet",
        label: "Hygrométrie ext.",
        value: formatRange(indicators.humidityOutside, "%"),
      };
    case "co2":
      return {
        faIcon: "smog",
        label: "CO₂",
        value: formatRange(indicators.co2, " ppm"),
      };
    case "nh3":
      return {
        faIcon: "smog",
        label: "NH₃",
        value: formatRange(indicators.nh3, " ppm"),
      };
    case "airflow":
      return {
        faIcon: "wind",
        label: "Débit d'air",
        value: formatValue(indicators.airflow, " m³"),
      };
    case "light":
      return {
        faIcon: "sun",
        label: "Éclairement",
        value:
          indicators.light === undefined
            ? "—"
            : `${indicators.light.toFixed(0)} min`,
        hint: "Temps d'allumage cumulé sur le dernier jour mesuré, en minutes",
      };
    case "animalsTotal":
      return {
        icon: "chicken",
        label: `Effectif${
          indicators.kinds && indicators.kinds.length > 1
            ? ` (${indicators.kinds.length} genres)`
            : ""
        }`,
        value:
          indicators.animalsTotal === undefined
            ? "—"
            : indicators.animalsTotal.toLocaleString("fr-FR"),
      };
    case "weight":
      return {
        faIcon: "gauge",
        label: "Poids moyen",
        value: formatValue(indicators.weightAverage, " g"),
      };
    case "gmq":
      return {
        faIcon: "chart-line",
        label: "GMQ",
        value: formatValue(indicators.gmq, " g/j"),
        hint: "Gain moyen quotidien",
      };
    case "homogeneity":
      return {
        faIcon: "arrows-up-down",
        label: "Homogénéité",
        value: formatValue(indicators.homogeneity, "%"),
      };
    case "panel":
      return {
        faIcon: "check",
        label: "Panel pesé",
        value:
          indicators.panel === undefined
            ? "—"
            : indicators.panel.toLocaleString("fr-FR"),
        hint: "Nombre d'animaux pesés",
      };
    case "dead":
      return {
        faIcon: "triangle-exclamation",
        label: "Mortalité / 7 j",
        value:
          indicators.dead?.total === undefined
            ? "—"
            : indicators.dead.total.toLocaleString("fr-FR"),
        hint: "Cumul sur la période affichée, et non depuis le début du lot",
      };
    case "water":
      return {
        faIcon: "glass-water",
        label: "Eau / jour",
        hint: "Consommation journalière moyenne sur la période",
        value: `${formatValue(indicators.waterTotalLiters, " L")}${
          indicators.waterPerHeadMl !== undefined
            ? ` · ${indicators.waterPerHeadMl.toFixed(0)} mL/tête`
            : ""
        }`,
      };
    case "food":
      return {
        faIcon: "wheat-awn",
        label: "Aliment / jour",
        hint: "Consommation journalière moyenne sur la période",
        value: `${formatValue(indicators.feedTotalKg, " kg")}${
          indicators.feedPerHeadG !== undefined
            ? ` · ${indicators.feedPerHeadG.toFixed(0)} g/tête`
            : ""
        }`,
      };
    case "waterFeedRatio":
      return {
        faIcon: "gauge",
        label: "Ratio eau / aliment",
        value:
          indicators.waterFeedRatio === undefined
            ? "—"
            : `${indicators.waterFeedRatio.toFixed(2)} L/kg`,
      };
    case "silos":
      return {
        faIcon: "wheat-awn",
        label: "Silos",
        hint: "Stock restant · consommation du jour",
        value:
          !indicators.silos || indicators.silos.length === 0
            ? "—"
            : indicators.silos
                .map((silo) =>
                  silo.stock === undefined ? "—" : `${silo.stock.toFixed(0)} kg`
                )
                .join(" · "),
      };
    case "gaz":
      return {
        faIcon: "cloud",
        label: "Gaz / jour",
        hint: "Consommation journalière moyenne sur la période",
        value: formatValue(indicators.gaz, " m³"),
      };
    case "electricity":
      return {
        faIcon: "gauge",
        label: "Électricité / jour",
        hint: "Consommation journalière moyenne sur la période",
        value: formatValue(indicators.electricity, " kW"),
      };
    case "siloStock":
      // Traçable sur la courbe, mais aussi affichable en ligne : le stock TOTAL des silos.
      return {
        faIcon: "wheat-awn",
        label: "Stock silo (total)",
        value: formatValue(
          indicators.silos
            ?.map((silo) => silo.stock)
            .filter((stock): stock is number => stock !== undefined)
            .reduce<number | undefined>(
              (sum, stock) => (sum ?? 0) + stock,
              undefined
            ),
          " kg"
        ),
      };
  }
}

const BuildingTrendChart = ({
  data,
  chartType,
  compact = false,
}: {
  data: RegulatorChartPoint[];
  chartType: RegulatorChartType;
  compact?: boolean;
}) => {
  const config = CHART_CONFIG[chartType];
  const hasBand = data.some((point) => point.min !== undefined);
  const values = data.flatMap((point) =>
    [point.value, point.min, point.max].filter(
      (value): value is number => value !== undefined
    )
  );
  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = (max - min) * 0.12 || Math.abs(max) * 0.05 || 1;

  // Recharts empile les Area : pour dessiner une bande [min, max] on trace une base
  // transparente (min) puis l'épaisseur (max - min) par-dessus.
  const chartData = data.map((point) => ({
    ...point,
    bandBase: point.min,
    bandSize:
      point.min !== undefined && point.max !== undefined
        ? point.max - point.min
        : undefined,
  }));

  return (
    <div className={compact ? "h-16 w-full" : "h-24 w-full"}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 4, right: 4, left: -4, bottom: 0 }}
        >
          {/* Accessibilité : graduations à 12px (au lieu de 9, illisible) sur `grey-readable`
              (#666, 5.74:1) au lieu de #939393 (3.07:1). `width` de l'axe Y élargi en
              conséquence, sinon les graduations plus grosses sont tronquées. */}
          <CartesianGrid stroke="#E6E6E6" strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 12, fill: "#666666" }}
            stroke="#666666"
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[min - padding, max + padding]}
            tick={{ fontSize: 12, fill: "#666666" }}
            stroke="#666666"
            width={38}
            tickFormatter={(value: number) =>
              Number.isInteger(value) ? String(value) : value.toFixed(1)
            }
          />
          <Tooltip
            formatter={(value: number, name: string) => {
              if (name === "bandBase" || name === "bandSize") return [];
              return [
                `${value.toFixed(config.digits ?? 0)}${config.unit}`,
                config.label,
              ];
            }}
            labelFormatter={(label) => `Jour : ${label}`}
          />
          {hasBand && (
            <>
              <Area
                dataKey="bandBase"
                stackId="band"
                stroke="none"
                fill="transparent"
                isAnimationActive={false}
                legendType="none"
              />
              <Area
                dataKey="bandSize"
                stackId="band"
                stroke="none"
                fill={config.color}
                fillOpacity={0.18}
                isAnimationActive={false}
                legendType="none"
              />
            </>
          )}
          <Line
            type="monotone"
            dataKey="value"
            name={config.label}
            stroke={config.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const RegulatorWidget = ({
  deviceUuid,
  name,
  tag = "Serenity",
  status = "ok",
  layoutVariant = "full",
  metrics = [],
  primaryMetric,
  secondaryMetric,
  showChart = true,
  chartType = "temperatureInside",
}: RegulatorWidgetProps) => {
  const navigate = useNavigate();
  const isPreview = useContext(WidgetPreviewContext);
  // Le type de compte détermine l'espace, donc la route distante : `/admin/device/…` renverrait
  // un éleveur sur son accueil (RoleRoute).
  const { getAccountStorage } = useAccountStorage();
  const accountType = getAccountStorage()?.type;
  const { t } = useTranslation();
  // Pastille et œil : état VPN réel de la box. Une seule requête pour tous les widgets de la
  // page (réponse mise en cache 30 s).
  const connections = useDeviceConnections();
  const connection = deviceUuid ? connections[deviceUuid] : undefined;
  const remoteBlocked = isRemoteBlocked(connection);

  // Widget PASSIF (décision UX du 2026-07-30) : aucun état local, aucun clic à faire. Tout
  // vient de la configuration — l'éleveur ouvre son dashboard et lit ses 8 grandeurs.
  const {
    buildingId,
    indicators,
    chartHistory,
    daysCount,
    latestDay,
    latestDate,
    noOpenBatch,
    batchName,
    window: measureWindow,
    source,
    deviceType,
    granted,
    hasGroupAccess,
    loading,
    error,
    reload,
  } = useRegulatorMeasures(deviceUuid, chartType);

  // Grandeur dont le droit bigdata manque : BIGDATA la masque, elle n'affichera jamais rien.
  const denied = (metric: string) => {
    const required = metricAccessRight(metric, deviceType);
    return (
      hasGroupAccess && required !== undefined && !granted.includes(required)
    );
  };
  const deniedLabel = t("regulator-metric-denied");

  const styles = statusStyles[status];
  const config = CHART_CONFIG[chartType];

  // Une vedette n'est retenue que si elle fait partie des grandeurs cochées : sinon la config
  // pointerait vers une grandeur qui n'est plus affichée (cas d'un décochage côté serveur).
  const highlights = [primaryMetric, secondaryMetric].filter(
    (metric): metric is RegulatorMetric =>
      metric !== undefined && metrics.includes(metric)
  );
  const secondary = metrics.filter((metric) => !highlights.includes(metric));

  // Sous-titre : nom du lot + jour d'élevage, l'info de contexte que l'éleveur cherche.
  // Sans nom de lot, le type réel du produit (« Serenity » était affiché même pour un Avibox).
  const productLabel = deviceType
    ? deviceType.charAt(0).toUpperCase() + deviceType.slice(1)
    : tag;
  const subtitle = noOpenBatch
    ? t("regulator-no-open-batch")
    : [
        batchName || productLabel,
        latestDay !== undefined ? `J${latestDay}` : null,
      ]
        .filter(Boolean)
        .join(" · ");

  // La pastille dit si la box est connectée ; le badge garde le niveau d'alerte (`status`).
  const statusDot = (
    <ConnectionDot connection={connection} className="w-2.5 h-2.5" />
  );
  const statusBadge = styles.badge ? (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles.badge}`}
    >
      {styles.badgeText}
    </span>
  ) : null;

  // Œil « connexion à distance » : ouvre la vue noVNC du régulateur, comme « Mes produits ».
  // Même icône que la liste des produits (DeviceRow/EntityCard), pour le même geste.
  // Masqué — et non désactivé — si l'uuid manque ou si l'espace de l'utilisateur n'a pas de
  // route distante : un bouton mort inquiète plus qu'une absence de bouton.
  // Jamais dans l'aperçu du configurateur : le clic ferait quitter la page en cours d'édition.
  const remoteRoute =
    deviceUuid && !isPreview
      ? remoteDeviceRoute(deviceUuid, accountType)
      : undefined;
  const remoteButton = remoteRoute ? (
    <button
      type="button"
      // Le nom du bâtiment voyage dans le state : la page distante ne connaît que des devices,
      // et l'éleveur reconnaît « Poulailler 3 », pas le nom du régulateur.
      onClick={() => navigate(remoteRoute, { state: { buildingName: name } })}
      // Grisé — pas masqué — quand la box est déconnectée : c'est passager, et l'info-bulle
      // dit pourquoi le bouton ne répond pas.
      disabled={remoteBlocked}
      className={
        remoteBlocked
          ? "text-grey-medium cursor-not-allowed p-1.5 rounded-full"
          : "text-grey-readable hover:text-tertiary hover:bg-grey-light/60 transition-colors p-1.5 rounded-full"
      }
      aria-label={
        remoteBlocked
          ? t("remote-view-blocked-disconnected")
          : t("remote-view-open")
      }
      title={
        remoteBlocked
          ? t("remote-view-blocked-disconnected")
          : t("remote-view-open")
      }
    >
      <FontAwesomeIcon icon="eye" className="h-4 w-4" />
    </button>
  ) : null;

  const headerAction = (
    <div className="flex items-center gap-1">
      {statusBadge}
      {remoteButton}
    </div>
  );

  const dayMonth = (date: string) => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

  // Ordre : l'erreur AVANT « aucun bâtiment » — une réponse en échec n'a pas de bâtiment, et le
  // widget affichait « aucun bâtiment » pour un 403, un 404 ou une panne. `""` = rien à dire
  // (session expirée : fetchWithToken renvoie déjà vers la connexion).
  const notice: string | null = (() => {
    if (!deviceUuid) return t("regulator-none-selected");
    if (loading) return t("regulator-loading");
    switch (error) {
      case "unauthorized":
        return "";
      case "forbidden":
        return t("regulator-access-denied");
      case "not-found":
        return t("regulator-not-found");
      case "invalid":
        return t("regulator-display-impossible");
      case "unavailable":
        return t("regulator-data-unavailable");
    }
    if (buildingId === undefined) return t("regulator-no-building");
    if (noOpenBatch) return t("regulator-no-open-batch");
    if (source === "legacy" && !isLegacyFirmwareType(deviceType))
      return t("regulator-legacy-soon");
    if (daysCount === 0 && measureWindow) {
      return t("regulator-no-measure-window", {
        from: dayMonth(measureWindow.from),
        to: dayMonth(measureWindow.to),
      });
    }
    return null;
  })();

  return (
    <WidgetShell
      // Pas de `detailRoute` : `/building/{id}` N'EXISTE PAS dans le routeur (cf. App.tsx, la
      // route BuildingsTable est commentée) et il n'y a aucune route `*`, donc le clic menait
      // sur un écran BLANC. Le widget reste non cliquable jusqu'à ce que la page bâtiment
      // existe ; la connexion à distance passe par l'œil du bandeau. (2026-07-30)
      title={name}
      subtitle={subtitle}
      titleIcon={statusDot}
      action={headerAction}
    >
      {notice !== null ? (
        <div className="h-full flex flex-col items-center justify-center gap-2 text-sm text-grey-readable text-center px-2">
          {notice}
          {error === "unavailable" && (
            <button
              type="button"
              onClick={reload}
              className="text-tertiary underline hover:no-underline"
            >
              {t("regulator-retry")}
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col h-full gap-2">
          {/* Date de référence : les grandeurs d'ambiance viennent de ce jour-là. Sans ce
              rappel, un régulateur muet depuis 3 jours afficherait des chiffres qu'on
              croirait actuels. */}
          <p
            className="text-xs text-grey-readable"
            title={`Relevés du dernier jour mesuré ; consommations et énergie en moyenne journalière sur ${daysCount} jour(s).`}
          >
            {latestDate
              ? `Au ${latestDate.slice(8, 10)}/${latestDate.slice(5, 7)}`
              : `Sur ${daysCount} jour${daysCount > 1 ? "s" : ""}`}
          </p>
          <div className="min-h-0 flex-1 overflow-auto space-y-2">
            {metrics.length === 0 ? (
              <p className="text-sm text-grey-readable py-2">
                Aucune grandeur sélectionnée — modifiez le widget pour en
                choisir.
              </p>
            ) : (
              <>
                {/* Vedettes : gros chiffres en tête, ce que l'éleveur lit en premier. */}
                {highlights.length > 0 && (
                  <div className="flex gap-2">
                    {highlights.map((metric) => {
                      const line = renderMetric(metric, indicators);
                      const { main, detail } = denied(metric)
                        ? { main: deniedLabel, detail: undefined }
                        : splitForHighlight(metric, indicators);
                      return (
                        <HighlightTile
                          key={metric}
                          label={line.label}
                          main={main}
                          detail={detail}
                          faIcon={line.faIcon}
                          icon={line.icon}
                          hint={line.hint}
                          color={metricColor(metric)}
                          wide={highlights.length === 1}
                        />
                      );
                    })}
                  </div>
                )}

                {/* Secondaires : grille 2 colonnes, deux fois plus dense qu'une liste. */}
                {secondary.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {secondary.map((metric) => {
                      const line = renderMetric(metric, indicators);
                      return (
                        <MetricLine
                          key={metric}
                          icon={line.icon}
                          faIcon={line.faIcon}
                          label={line.label}
                          value={denied(metric) ? deniedLabel : line.value}
                          hint={line.hint}
                          color={metricColor(metric)}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {showChart && (
            <div className="mt-auto pt-1 border-t border-grey-light">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-sm font-oxaniumMedium text-default-text truncate">
                  {config.label}
                </p>
                {chartHistory.length > 0 && (
                  <span className="text-sm text-grey-readable shrink-0">
                    {formatValue(
                      chartHistory[chartHistory.length - 1].value,
                      config.unit,
                      config.digits ?? 0
                    )}
                  </span>
                )}
              </div>
              {chartHistory.length === 0 ? (
                <div
                  className={`${
                    layoutVariant === "compact" ? "h-16" : "h-24"
                  } w-full flex items-center justify-center text-sm text-grey-readable text-center px-2`}
                >
                  {config.label} :{" "}
                  {denied(chartType)
                    ? deniedLabel
                    : t("regulator-metric-not-measured")}
                </div>
              ) : (
                <BuildingTrendChart
                  data={chartHistory}
                  chartType={chartType}
                  compact={layoutVariant === "compact"}
                />
              )}
            </div>
          )}
        </div>
      )}
    </WidgetShell>
  );
};

/** Réexport pour les consommateurs qui lisaient la plage depuis ce module. */
export { rangeValue };
